import { memo } from 'react';
import PropTypes from 'prop-types';
import { isNil, last, noop, toString } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Components
import AccessControl from 'components/common/AccessControl';
import ActionLink from 'components/common/ActionLink';
// Import UI Components
import { Button, Tooltip } from '@geneui/components';
// Import Hooks
import { useGTMEvent } from 'hooks/UseGTM';
// Import Enums
import { ABTestStatusEnum, PermissionEnum } from 'enums';
// Import Constants
import {
    StateModesLabels,
    CampaignStatusLabels,
    l,
    ActionsIcons,
    GACategoryEvents,
    PARTNER_SETTINGS,
} from 'constants/common';

const { GADetailsCategory } = GACategoryEvents;

type PermissionsProps = Record<string, string | string[] | Record<string, string | string[]>>;

const CampaignActionsButtons = (props) => {
    const {
        data,
        unArchiveCampaignAction,
        archiveCampaignAction,
        deleteCampaignAction,
        getCloneCampaignPath,
        getEditCampaignPath,
        getViewCampaignReportPath,
        infoCampaignAction,
        stopSendCampaignAction,
        stopABTestCampaignAction,
        isInInfoModal,
        isInReportPage,
        NextRunDate,
        handleInfoModalActionClick,
    } = props;
    const { State, Status, IsNonMarketing, ArchivedDate, ABTests } = data;
    const latestABTest = last(ABTests);
    // TODO: Create a type for latestABTest
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { Status: ABTestStatus } = latestABTest || ({} as any);
    const status = toString(Status);

    const { t } = useTranslation();
    const { logEvent } = useGTMEvent();

    const isArchived = !isNil(ArchivedDate);
    const isDraft = State === StateModesLabels.Draft;
    const isCompleted = status === CampaignStatusLabels.Completed;
    const isStopped = status === CampaignStatusLabels.Stopped;
    const isInProgress = status === CampaignStatusLabels.InProgress;
    const isIdle = status === CampaignStatusLabels.Idle;

    const isVisibleStopABTestButton = () => ABTestStatus === ABTestStatusEnum.InProgress && !isInInfoModal;
    const isVisibleDelete = () => !isArchived && !isCompleted && !isStopped && !isInProgress && !isIdle;
    const isVisibleEdit = () =>
        !isArchived &&
        !isCompleted &&
        !isStopped &&
        !isInProgress &&
        !(status === CampaignStatusLabels.Error && isNil(NextRunDate));
    const isVisibleArchive = () => (!isArchived && isCompleted) || (!isArchived && isStopped);
    const isVisibleStopSend = () => !isArchived && !isCompleted && !isStopped && !isDraft;

    let permissionsProps: PermissionsProps = { permissions: PermissionEnum.EditCampaign };
    if (IsNonMarketing) {
        permissionsProps = {
            mandatoryPermissions: [PermissionEnum.EditCampaign, PermissionEnum.ManageNonMarketingCampaign],
        };
    }

    const onInfoAction = () => {
        logEvent(
            GADetailsCategory.name,
            GADetailsCategory.events.campaignInfoActionButton,
            GADetailsCategory.labels.campaignInfoActionButton,
            1,
        );
        infoCampaignAction(data);
    };

    const onReportAction = () => {
        logEvent(
            GADetailsCategory.name,
            GADetailsCategory.events.campaignReportActionButton,
            GADetailsCategory.labels.campaignReportActionButton,
            1,
        );

        handleInfoModalActionClick();
    };

    return (
        <>
            {isVisibleStopABTestButton() && (
                <AccessControl partnerSetting={PARTNER_SETTINGS.IsAllowABTest}>
                    <Tooltip text={t(l.StopABTest)}>
                        <Button
                            color="primary"
                            flexibility="default"
                            appearance="minimal"
                            icon="bc-icon-test-stop"
                            onClick={() => stopABTestCampaignAction(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleDelete() && (
                <AccessControl permissions={PermissionEnum.DeleteCampaign}>
                    <Tooltip text={t(l.DeleteCampaign)}>
                        <Button
                            icon={ActionsIcons.Delete}
                            appearance="minimal"
                            color="primary"
                            flexibility="default"
                            onClick={() => deleteCampaignAction(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleEdit() && (
                <AccessControl {...permissionsProps}>
                    <Tooltip text={t(l.EditCampaign)}>
                        <ActionLink
                            icon={ActionsIcons.Edit}
                            appearance="minimal"
                            color="primary"
                            flexibility="default"
                            redirectPath={getEditCampaignPath(data)}
                            onRedirect={handleInfoModalActionClick}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isArchived && (
                <Tooltip text={t(l.UnarchiveCampaign)}>
                    <Button
                        icon={ActionsIcons.UnArchive}
                        appearance="minimal"
                        color="primary"
                        flexibility="default"
                        onClick={() => unArchiveCampaignAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleArchive() && (
                <Tooltip text={t(l.ArchiveCampaign)}>
                    <Button
                        icon={ActionsIcons.Archive}
                        appearance="minimal"
                        color="primary"
                        flexibility="default"
                        onClick={() => archiveCampaignAction(data)}
                    />
                </Tooltip>
            )}
            {!isDraft && !isInReportPage && (
                <Tooltip text={t(l.CampaignReport)}>
                    <ActionLink
                        icon={ActionsIcons.ViewReport}
                        appearance="minimal"
                        color="primary"
                        flexibility="default"
                        redirectPath={getViewCampaignReportPath(data)}
                        onRedirect={onReportAction}
                    />
                </Tooltip>
            )}
            {isVisibleStopSend() && (
                <AccessControl permissions={PermissionEnum.ExecuteCampaign}>
                    <Tooltip text={t(l.StopCampaign)}>
                        <Button
                            icon={ActionsIcons.StatusStop}
                            appearance="minimal"
                            color="primary"
                            flexibility="default"
                            onClick={() => stopSendCampaignAction(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            <AccessControl permissions={PermissionEnum.CreateCampaign}>
                <Tooltip text={t(l.CloneCampaign)}>
                    <ActionLink
                        icon={ActionsIcons.Clone}
                        appearance="minimal"
                        color="primary"
                        flexibility="default"
                        redirectPath={getCloneCampaignPath(data)}
                        onRedirect={handleInfoModalActionClick}
                    />
                </Tooltip>
            </AccessControl>
            {infoCampaignAction && (
                <Tooltip text={t(l.CampaignInfo)}>
                    <Button
                        icon={ActionsIcons.Stop}
                        appearance="minimal"
                        color="primary"
                        flexibility="default"
                        onClick={onInfoAction}
                    />
                </Tooltip>
            )}
        </>
    );
};

CampaignActionsButtons.propTypes = {
    data: PropTypes.object.isRequired,
    unArchiveCampaignAction: PropTypes.func,
    archiveCampaignAction: PropTypes.func,
    deleteCampaignAction: PropTypes.func,
    getCloneCampaignPath: PropTypes.func,
    getViewCampaignReportPath: PropTypes.func,
    stopSendCampaignAction: PropTypes.func,
    getEditCampaignPath: PropTypes.func,
    infoCampaignAction: PropTypes.func,
    stopABTestCampaignAction: PropTypes.func,
    isInInfoModal: PropTypes.bool,
    isInReportPage: PropTypes.bool,
    NextRunDate: PropTypes.string,
    handleInfoModalActionClick: PropTypes.func,
};

CampaignActionsButtons.defaultProps = {
    unArchiveCampaignAction: noop,
    archiveCampaignAction: noop,
    deleteCampaignAction: noop,
    getCloneCampaignPath: noop,
    getEditCampaignPath: noop,
    getViewCampaignReportPath: noop,
    stopSendCampaignAction: noop,
    stopABTestCampaignAction: noop,
    isInInfoModal: false,
    isInReportPage: false,
    handleInfoModalActionClick: noop,
};

export default memo(CampaignActionsButtons);
